import React from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import { View } from '@24i/nxg-sdk-quarks';
import { withWebHeader } from '@24i/nxg-sdk-smartott/src/navigation/components/withWebHeader';
import { firstLetterUppercase } from '@24i/nxg-core-utils';
import SettingsDropdownPicker from '@24i/nxg-sdk-smartott/src/components/SettingsDropdownPicker';
import { useFeature } from '@24i/nxg-sdk-smartott-shared/src/context/AppSettingsData';
import { Storage } from '@24i/nxg-sdk-quantum';
import { useTheme } from '@24i/nxg-sdk-higgs';
import { getTelegramWebApp } from '@24i/nxg-sdk-higgs/src/utils';
import { log } from '@24i/nxg-core-utils/src/logger';
import { ASYNC_STORAGE_KEY_I18_NEXT_LANG } from '@24i/nxg-core-utils/src/constants';
import { useThemeFetch } from '@24i/nxg-sdk-smartott-shared/src/hooks/query/theme/useThemeFetch';
import { useMenuFetch } from '@24i/nxg-sdk-smartott-shared/src/hooks/query/menu/useMenuFetch';
import type { Theme as ThemeType } from '@24i/nxg-sdk-photon';

import Select from '../../../components/Select';
import getLanguageScreenStyles from '../styles';
import { useShouldShowTopNavbar } from '../../../hooks/useShouldShowTopNavbar';

const LanguageScreen = () => {
    const { t, i18n: i18nTranslation } = useTranslation();
    const { shouldShowTopNavbar } = useShouldShowTopNavbar();
    const supportedLanguages = useFeature('locale')?.supportedLanguages;
    const defaultLanguageList = Object.keys(i18n.options.resources || {});
    const languagesOrdered = defaultLanguageList.filter(
        (lang) =>
            !supportedLanguages ||
            supportedLanguages.length === 0 ||
            supportedLanguages?.includes(lang)
    );
    const { theme, setTheme } = useTheme();
    const getStyles = getLanguageScreenStyles;
    const styles = getStyles(theme);
    const { fetch: fetchTheme } = useThemeFetch();
    const { fetch: fetchMenu } = useMenuFetch();

    const getTestID = (index): string => {
        return `${t(`common.${languagesOrdered[index]}`).toLowerCase()}_button`;
    };
    const setNewTheme = async () => {
        try {
            const fetchedTheme = (await fetchTheme({ forceRefresh: true })) as ThemeType;
            setTheme(fetchedTheme);
        } catch (err) {
            log(err);
        }
    };
    i18n.on('languageChanged', () => {
        setNewTheme();
        fetchMenu({ forceRefresh: true });
    });

    const onLanguageChange = (selectedLanguage) => {
        // The line below keeps in sync the user-language and i18nextLng values inside localStorage
        Storage.setItem(ASYNC_STORAGE_KEY_I18_NEXT_LANG, selectedLanguage);
        i18nTranslation.changeLanguage(selectedLanguage);
    };

    const onLanguageChangeByIndex = (index: number) => {
        const selectedLanguage = languagesOrdered[index];
        onLanguageChange(selectedLanguage);
    };

    return !shouldShowTopNavbar ? (
        <View style={styles.selectContainer} nativeId="language_screen">
            {getTelegramWebApp() ? (
                <style global jsx>{`
                    div#__next {
                        touch-action: none !important;
                    }
                `}</style>
            ) : null}
            <Select
                title={t('settings.language.description')}
                options={languagesOrdered.map((lang) => {
                    // const inCurrentLanguage = firstLetterUppercase(t(`settings.language.${lang}`));
                    const inItsLanguage = t(`settings.language.${lang}`, { lng: lang });
                    const inSelectedLanguage = t(`settings.language.${lang}`, {
                        lng: i18nTranslation.language,
                    });

                    return {
                        testID: `${t(`common.${lang}`).toLowerCase()}_button`,
                        title: inItsLanguage.charAt(0).toUpperCase() + inItsLanguage.slice(1),
                        value: lang,
                        subtitle:
                            inSelectedLanguage.charAt(0).toUpperCase() +
                            inSelectedLanguage.slice(1),
                    };
                })}
                initialValue={i18nTranslation.language}
                onChange={onLanguageChange}
                isLandscape={false}
            />
        </View>
    ) : (
        <SettingsDropdownPicker
            options={languagesOrdered.map((lang) => {
                // const inCurrentLanguage = firstLetterUppercase(t(`settings.language.${lang}`));
                const inItsLanguage = firstLetterUppercase(
                    t(`settings.language.${lang}`, { lng: lang })
                );
                return inItsLanguage;
            })}
            selectedItem={firstLetterUppercase(t(`settings.language.${i18nTranslation.language}`))}
            onSelectionPress={(index) => onLanguageChangeByIndex(index)}
            title={t('settings.language.description')}
            buttonTestID="app_language_picker"
            getItemsTestID={getTestID}
        />
    );
};

export default withWebHeader(LanguageScreen);
